import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronDown, AlarmClock } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const mondayOf = (d) => { const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())); x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7)); return x; };
const addDays = (s, n) => { const x = new Date(`${s}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return iso(x); };
const nice = (s) => new Date(`${s}T12:00:00Z`).toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short" });

function CleanerRow({ w }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`ea-row-${w.workerId}`}>
      <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50" onClick={() => setOpen(!open)} data-testid={`ea-toggle-${w.workerId}`}>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="flex-1 font-semibold text-slate-800">{w.name}</span>
        <span className="text-xs text-slate-500">{w.days} day{w.days === 1 ? "" : "s"}</span>
        <span className="min-w-[2.5rem] text-center rounded-full bg-orange-100 text-orange-700 text-sm font-bold px-2 py-0.5" data-testid={`ea-count-${w.workerId}`}>{w.count}</span>
      </button>
      {open && (
        <div className="px-11 pb-3 space-y-1">
          {w.attempts.map((a, i) => (
            <div key={i} className="flex flex-wrap gap-x-4 text-sm text-slate-600" data-testid={`ea-attempt-${w.workerId}-${i}`}>
              <span className="w-28 font-medium text-slate-700">{nice(a.day)}</span>
              <span className="w-36 truncate">{a.site}</span>
              <span>tried <b>{a.time}</b> · allowed from {a.allowedFrom}</span>
              <span className="text-orange-700 font-medium">{a.minutesEarly} min early</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EarlyArrivals() {
  const [week, setWeek] = useState(iso(mondayOf(new Date())));
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    client.get(`/attendance/early-arrivals?weekStart=${week}`).then((r) => setData(r.data)).catch(() => setData({ cleaners: [], total: 0 }));
  }, [week]);
  return (
    <div className="space-y-5" data-testid="early-arrivals-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-brand-ink flex items-center gap-2"><AlarmClock className="h-7 w-7 text-brand" />Early arrivals</h1>
        <p className="text-sm text-slate-500 mt-1">Cleaners who tried to clock in before their allowed time, week by week.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="icon" onClick={() => setWeek(addDays(week, -7))} data-testid="ea-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="font-semibold text-slate-800 min-w-[200px] text-center" data-testid="ea-week-label">{nice(week)} – {nice(addDays(week, 6))}</span>
        <Button variant="outline" size="icon" onClick={() => setWeek(addDays(week, 7))} data-testid="ea-next-week"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => setWeek(iso(mondayOf(new Date())))} data-testid="ea-this-week">This week</Button>
        {data && <span className="ml-auto text-sm text-slate-500" data-testid="ea-total">{data.total} early attempt{data.total === 1 ? "" : "s"}</span>}
      </div>
      <Card className="border-slate-200 overflow-hidden">
        {!data ? <p className="p-6 text-sm text-slate-400">Loading…</p>
          : !data.cleaners.length ? <p className="p-6 text-sm text-slate-400" data-testid="ea-empty">No early clock-in attempts this week.</p>
          : data.cleaners.map((w) => <CleanerRow key={w.workerId} w={w} />)}
      </Card>
    </div>
  );
}
